<template>
    <AppLayout>
        <div class="max-w-7xl mx-auto px-4 py-6">
            <!-- Page Title -->
            <div class="mb-6">
                <h1 class="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                    {{ $tc('LBL_INVOICE_REPORT', 2) }}
                </h1>
                <!-- Breadcrumb -->
                <nav
                    v-if="!isRetailer"
                    class="text-sm text-gray-500 mb-6"
                    aria-label="Breadcrumb"
                >
                    <GoBack />
                </nav>
            </div>
            <!-- breadcrumb end -->

            <div
                class="w-full bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-visible"
            >
                <div class="flex flex-col md:items-center md:flex-row justify-between px-2 py-2">
                    <div class="flex flex-col p-2">
                        <p class="font-semibold">
                            Order for <span class="text-green-600">{{ bills[0]?.user.name }}</span>
                        </p>

                        <p class="text-xs text-gray-400">{{ bills?.length }} Order/s Placed</p>
                    </div>
                    <!-- Search & Action Bar -->
                    <!-- Action Buttons -->
                    <div class="flex justify-end">
                        <a
                            target="_blank"
                            :href="route('export.retailer.report', retailerId)"
                            class="px-3 py-2 border border-red rounded-md cursor-pointer hover:bg-green-200"
                            rel="noopener noreferrer"
                        >
                            Export Excel
                        </a>
                    </div>
                </div>

                <div
                    class="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-none shadow-md"
                >
                    <div class="overflow-x-auto">
                        <table
                            class="min-w-[300px] w-full table-auto text-sm sm:text-base text-left text-gray-700 dark:text-gray-300"
                        >
                            <tbody>
                                <template v-for="(group, groupLabel) in groupedBillsData">
                                    <tr
                                        v-if="group.length"
                                        class="bg-gray-200 dark:bg-gray-700"
                                    >
                                        <td
                                            colspan="8"
                                            class="px-4 py-3 font-semibold"
                                        >
                                            <div class="flex justify-between items-center w-full">
                                                <span>{{ groupLabel }}</span>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr
                                        class="bg-white rounded-xl shadow border border-gray-200"
                                        v-if="group.length"
                                        v-for="bill in group"
                                        :key="bill.id"
                                        :class="[
                                            'border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700',
                                            bill.delivery_status === 'Placed'
                                                ? 'bg-red-50 dark:bg-red-900/30'
                                                : bill.delivery_status === 'Packed'
                                                ? 'bg-yellow-50 dark:bg-yellow-900/30'
                                                : bill.delivery_status === 'Shipped'
                                                ? 'bg-blue-50 dark:bg-blue-900/30'
                                                : bill.delivery_status === 'Delivered'
                                                ? 'bg-green-50 dark:bg-green-900/30'
                                                : '',
                                        ]"
                                    >
                                        <td class="p-4 w-full">
                                            <!-- GRID layout for alignment -->
                                            <div
                                                class="grid grid-cols-1 md:grid-cols-[30%_10%_20%_10%_20%] gap-4 items-start"
                                            >
                                                <!-- Order Info -->
                                                <div class="space-y-1">
                                                    <p class="text-sm text-gray-500">Order Placed</p>
                                                    <p class="font-medium text-gray-800">
                                                        {{ bill.created_at | dateLabel }}
                                                    </p>
                                                </div>
                                                <div class="space-y-1">
                                                    <p class="text-sm text-gray-500">Total</p>
                                                    <p class="font-medium text-green-600">
                                                        {{ config(common.currency) }}
                                                        {{ sumAmountFromTransactions(bill.transactions) }}
                                                    </p>
                                                </div>
                                                <div class="space-y-1">
                                                    <p class="text-sm text-gray-500">{{ bill.delivery_status }} Date</p>
                                                    <p class="font-medium text-gray-800">{{ bill.updated_at | LLL }}</p>
                                                </div>
                                                <div class="space-y-1">
                                                    <p class="text-sm text-gray-500">Status</p>
                                                    <span
                                                        :class="
                                                            getPaymentStatus(bill) === 'Paid'
                                                                ? 'bg-green-400'
                                                                : 'bg-yellow-400'
                                                        "
                                                        class="inline-block px-2 py-1 text-xs font-semibold text-white rounded"
                                                        >{{
                                                            hasPendingUserPayments(bill)
                                                                ? 'Declared'
                                                                : getPaymentStatus(bill)
                                                        }}</span
                                                    >
                                                </div>
                                                <div class="space-y-1">
                                                    <p class="text-sm text-gray-500">
                                                        Order #
                                                        <span class="font-medium text-gray-800">{{
                                                            bill.bill_number
                                                        }}</span>
                                                    </p>

                                                    <!-- Action Links -->
                                                    <div
                                                        class="flex flex-wrap items-center justify-between gap-4 mt-1 space-x-2"
                                                    >
                                                        <t-button
                                                            variant="text"
                                                            class="text-sm text-blue-600"
                                                            @click="goto('account.show', { orderId: bill.bill_number })"
                                                        >
                                                            View Order Detail
                                                        </t-button>
                                                        <t-button
                                                            variant="text"
                                                            class="text-sm text-blue-600"
                                                            @click="printInvoice(bill)"
                                                        >
                                                            View Invoice
                                                        </t-button>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script>
    import moment from 'moment';
    import AppLayout from '@/Layouts/AppLayout.vue';
    import GoBack from '@/Components/GoBack.vue';
    import common from '@/common';

    export default {
        name: 'BillsOfRetailer',

        components: {
            AppLayout,
            GoBack,
        },

        props: {
            bills: {
                type: Array,
                required: true,
            },
            retailerId: {
                type: Number,
                required: true,
            },
        },

        data() {
            return {
                common,
            };
        },

        computed: {
            groupedBillsData() {
                console.log('bills', this.bills);
                const groups = {
                    Today: [],
                    Yesterday: [],
                    'This Week': [],
                    'Last Week': [],
                    Past: [],
                };

                const today = moment();
                const yesterday = moment().subtract(1, 'day');
                const startOfWeek = moment().startOf('week');
                const startOfLastWeek = moment().subtract(1, 'week').startOf('week');
                const endOfLastWeek = moment(startOfWeek).subtract(1, 'day');

                for (const order of this.bills || []) {
                    const billDate = moment(order.updated_at);

                    if (billDate.isSame(today, 'day')) {
                        groups['Today'].push(order);
                    } else if (billDate.isSame(yesterday, 'day')) {
                        groups['Yesterday'].push(order);
                    } else if (billDate.isSameOrAfter(startOfWeek)) {
                        groups['This Week'].push(order);
                    } else if (billDate.isBetween(startOfLastWeek, endOfLastWeek, 'day', '[]')) {
                        groups['Last Week'].push(order);
                    } else {
                        groups['Past'].push(order);
                    }
                }

                return groups;
            },
        },

        methods: {
            sumAmountFromTransactions(transactions) {
                let sumGrandTotal = 0;
                transactions.forEach((transaction) => {
                    sumGrandTotal += parseFloat(transaction.grand_total);
                });
                return sumGrandTotal.toFixed(2);
            },

            groupedBills() {
                const groupedBills = [
                    [], // today
                    [], // yesterday
                    [], // thisWeek
                    [], // lastWeek
                    [], // past
                ];

                const today = moment();
                const yesterday = moment().subtract(1, 'day');

                this.bills.forEach((bill) => {
                    if (moment(bill.created_at).isSame(today, 'day')) {
                        groupedBills[0].push(bill);
                    } else if (moment(bill.created_at).isSame(yesterday, 'day')) {
                        groupedBills[1].push(bill);
                    } else if (moment(bill.created_at).week() === moment().week()) {
                        groupedBills[2].push(bill);
                    } else if (moment(bill.created_at).week() === moment().subtract(1, 'week').week()) {
                        groupedBills[3].push(bill);
                    } else {
                        groupedBills[4].push(bill);
                    }
                });

                return groupedBills;
            },

            getPaymentStatus(bill) {
                return bill.payment_status;
            },

            getTotalQuantity(transactions) {
                let totalQty = 0;
                transactions.forEach((transaction) => {
                    const qty = parseFloat(transaction.qty);
                    totalQty += qty;
                });
                return totalQty;
            },

            hasPendingUserPayments(bill) {
                const pendingBills = bill.user_payments.filter(
                    (payment) => payment.accepted_at === null && payment.deleted_at === null,
                );
                return pendingBills.length;
            },

            printInvoice(bill) {
                this.newTab('invoice.print', { billId: bill.id });
            },
        },
    };
</script>
